import { getEmptyAuthentication } from 'reducers/authentication/authenticationSlice'
import { Authentication } from 'share/domain/authentication'
import {
  UpdateAccountResponseData,
  ResetPasswordResponse
} from 'share/mutations/account'
import { ApolloQueryResult } from '@apollo/client'
import { client } from 'app/App'
import {
  USER_LOGIN,
  UserLoginVariables,
  UserLoginResponse,
  GET_REFRESH_TOKEN,
  RefreshTokenResponse
} from 'share/queries/login'
import { Interaction } from 'scheduler/tracing'

export function formatDate(dateStr: string): string {
  try {
    const dt = new Date(dateStr)
    return dt.toLocaleDateString()
  } catch {
    return dateStr
  }
}

export function formatToUSDate(dateStr: string): string {
  try {
    const dt = new Date(dateStr)
    return dt.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  } catch {
    return dateStr
  }
}

export const phoneRegExp =
  /^((\+\d{1,3}(-| )?\(?\d\)?(-| )?\d{1,3})|(\(?\d{2,3}\)?))(-| )?(\d{3,4})(-| )?(\d{4})(( x| ext)\d{1,5}){0,1}$/

export const ssnRegExp = /^(\d{3}-?\d{2}-?\d{4}|XXX-XX-XXXX)$/

export const dropdownStateOptions = [
  { key: 'Select an state', value: '' },
  { key: 'Alabama', value: 'AL' },
  { key: 'Alaska', value: 'AK' },
  { key: 'Arizona', value: 'AZ' },
  { key: 'Arkansas', value: 'AR' },
  { key: 'California', value: 'CA' },
  { key: 'Colorado', value: 'CO' },
  { key: 'Connecticut', value: 'CT' },
  { key: 'Delaware', value: 'DE' },
  { key: 'Florida', value: 'FL' },
  { key: 'Georgia', value: 'GA' },
  { key: 'Hawaii', value: 'HI' },
  { key: 'Idaho', value: 'ID' },
  { key: 'Illinois', value: 'IL' },
  { key: 'Indiana', value: 'IN' },
  { key: 'Iowa', value: 'IA' },
  { key: 'Kansas', value: 'KS' },
  { key: 'Kentucky', value: 'KY' },
  { key: 'Louisiana', value: 'LA' },
  { key: 'Maine', value: 'ME' },
  { key: 'Maryland', value: 'MD' },
  { key: 'Massachusetts', value: 'MA' },
  { key: 'Michigan', value: 'MI' },
  { key: 'Minnesota', value: 'MN' },
  { key: 'Mississippi', value: 'MS' },
  { key: 'Missouri', value: 'MO' },
  { key: 'Montana', value: 'MT' },
  { key: 'Nebraska', value: 'NE' },
  { key: 'Nevada', value: 'NV' },
  { key: 'New Hampshire', value: 'NH' },
  { key: 'New Jersey', value: 'NJ' },
  { key: 'New Mexico', value: 'NM' },
  { key: 'New York', value: 'NY' },
  { key: 'North Carolina', value: 'NC' },
  { key: 'North Dakota', value: 'ND' },
  { key: 'Ohio', value: 'OH' },
  { key: 'Oklahoma', value: 'OK' },
  { key: 'Oregon', value: 'OR' },
  { key: 'Pennsylvania', value: 'PA' },
  { key: 'Rhode Island', value: 'RI' },
  { key: 'South Carolina', value: 'SC' },
  { key: 'South Dakota', value: 'SD' },
  { key: 'Tennessee', value: 'TN' },
  { key: 'Texas', value: 'TX' },
  { key: 'Utah', value: 'UT' },
  { key: 'Vermont', value: 'VT' },
  { key: 'Virginia', value: 'VA' },
  { key: 'Washington', value: 'WA' },
  { key: 'West Virginia', value: 'WV' },
  { key: 'Wisconsin', value: 'WI' },
  { key: 'Wyoming', value: 'WY' }
]

// Account Save Common Code Start

export enum AccountCreationStep {
  InitialStep = 1,
  EnteredTempPasswordStepCompleted = 2,
  CreatePasswordStepCompleted = 3,
  VerifyPhoneNumberStepCompleted = 4,
  VerifyAccountDetailsStepCompleted = 5,
  CompletedAllSteps = 6
}

export function loadAuthenticationFromInput(
  input: UpdateAccountResponseData,
  authentication: Authentication
): Authentication {
  const updatedAuthentication = getEmptyAuthentication()
  updatedAuthentication.isAuthenticated = authentication.isAuthenticated
  updatedAuthentication.idToken = authentication.idToken
  updatedAuthentication.accessToken = authentication.accessToken
  updatedAuthentication.expirationTime = authentication.expirationTime
  updatedAuthentication.requiresMfa = authentication.requiresMfa
  updatedAuthentication.sessionId = authentication.sessionId
  if (updatedAuthentication.account) {
    updatedAuthentication.account.id = input.id
    updatedAuthentication.account.givenName = input.givenName
    updatedAuthentication.account.middleName = input.middleName
    updatedAuthentication.account.familyName = input.familyName
    updatedAuthentication.account.suffix = input.suffix
    updatedAuthentication.account.homePhone = input.homePhone

    updatedAuthentication.account.mobilePhone = input.mobilePhone
    updatedAuthentication.account.workPhone = input.workPhone
    updatedAuthentication.account.userName = input.userName
    updatedAuthentication.account.emailAddress = input.emailAddress
    updatedAuthentication.account.street = input.street
    updatedAuthentication.account.zip = input.zip
    updatedAuthentication.account.city = input.city
    updatedAuthentication.account.state = input.state
    updatedAuthentication.account.ssn = input.ssn
    updatedAuthentication.account.caseCount = 22
    updatedAuthentication.account.contactType = input.contactType
  } else {
    updatedAuthentication.account = null
  }

  return updatedAuthentication
}

export function getInitializedUpdateAccountResponseData(): UpdateAccountResponseData {
  const result: UpdateAccountResponseData = {
    id: 0,
    givenName: '',
    middleName: null,
    familyName: '',
    suffix: null,
    homePhone: null,
    workPhone: null,
    mobilePhone: '',
    userName: '',
    emailAddress: null,
    street: null,
    city: null,
    state: null,
    zip: null,
    ssn: null,
    caseCount: 0,
    contactType: 0
  }
  return result
}

export function loadResultWithData(
  dest: UpdateAccountResponseData,
  source: UpdateAccountResponseData
): void {
  dest.success = true
  dest.id = source.id
  dest.givenName = source.givenName
  dest.middleName = source.middleName
  dest.familyName = source.familyName
  dest.suffix = source.suffix
  dest.homePhone = source.homePhone
  dest.workPhone = source.workPhone
  dest.mobilePhone = source.mobilePhone
  dest.userName = source.userName
  dest.emailAddress = source.emailAddress
  dest.street = source.street
  dest.city = source.city
  dest.state = source.state
  dest.zip = source.zip
  dest.ssn = source.ssn
  dest.caseCount = source.caseCount
  dest.contactType = source.contactType
}

export function obfuscatePhone(phoneNumber: string | null | undefined): string {
  if (!phoneNumber) return ''
  return `***-***-${phoneNumber.substr(phoneNumber.length - 4)}`
}

// Account Save Common Code Finish

export function obfuscateEmailAddress(
  emailAddress: string | null | undefined
): string {
  if (!emailAddress) {
    return ''
  }
  const mainParts = emailAddress.split('@')
  if (mainParts.length !== 2) {
    return ''
  }

  const firstLetter = mainParts[0].substring(0, 1)
  const secondLetter = mainParts[1].substring(0, 1)

  const secondaryParts = mainParts[1].split('.')

  if (secondaryParts.length != 2) {
    return ''
  }

  const result = `${firstLetter}***********@${secondLetter}****.${secondaryParts[1]}`
  return result
}

// Common Login Helpers Start

export async function loginUser(
  input: UserLoginVariables
): Promise<ApolloQueryResult<UserLoginResponse>> {
  return client.query<UserLoginResponse, UserLoginVariables>({
    query: USER_LOGIN,
    variables: input,
    fetchPolicy: 'no-cache'
  })
}

export async function getRefreshTokens(): Promise<
  ApolloQueryResult<RefreshTokenResponse>
> {
  return client.query<RefreshTokenResponse>({
    query: GET_REFRESH_TOKEN,
    fetchPolicy: 'no-cache'
  })
}

export function loadTheReduxStoreWithAuthenticationInfoHelper(
  input: UserLoginResponse
): Authentication {
  const authentication = getEmptyAuthentication()
  authentication.isAuthenticated = false
  authentication.idToken =
    input.authentication.tokenFromUsernameAndPassword.idToken
  authentication.accessToken =
    input.authentication.tokenFromUsernameAndPassword.accessToken
  authentication.expirationTime =
    input.authentication.tokenFromUsernameAndPassword.expirationTime
  authentication.requiresMfa =
    input.authentication.tokenFromUsernameAndPassword.requiresMfa
  authentication.mfaDeliveryDestination =
    input.authentication.tokenFromUsernameAndPassword.mfaDeliveryDestination
  authentication.sessionId =
    input.authentication.tokenFromUsernameAndPassword.sessionId

  if (
    authentication.account &&
    input.authentication.tokenFromUsernameAndPassword.account
  ) {
    authentication.account.id =
      input.authentication.tokenFromUsernameAndPassword.account.id
    authentication.account.givenName =
      input.authentication.tokenFromUsernameAndPassword.account.givenName
    authentication.account.middleName =
      input.authentication.tokenFromUsernameAndPassword.account.middleName
    authentication.account.familyName =
      input.authentication.tokenFromUsernameAndPassword.account.familyName
    authentication.account.suffix =
      input.authentication.tokenFromUsernameAndPassword.account.suffix
    authentication.account.homePhone =
      input.authentication.tokenFromUsernameAndPassword.account.homePhone

    authentication.account.mobilePhone =
      input.authentication.tokenFromUsernameAndPassword.account.mobilePhone
    authentication.account.workPhone =
      input.authentication.tokenFromUsernameAndPassword.account.workPhone
    authentication.account.userName =
      input.authentication.tokenFromUsernameAndPassword.account.userName
    authentication.account.emailAddress =
      input.authentication.tokenFromUsernameAndPassword.account.emailAddress
    authentication.account.street =
      input.authentication.tokenFromUsernameAndPassword.account.street
    authentication.account.zip =
      input.authentication.tokenFromUsernameAndPassword.account.zip
    authentication.account.city =
      input.authentication.tokenFromUsernameAndPassword.account.city
    authentication.account.state =
      input.authentication.tokenFromUsernameAndPassword.account.state
    authentication.account.ssn =
      input.authentication.tokenFromUsernameAndPassword.account.ssn
    authentication.account.caseCount =
      input.authentication.tokenFromUsernameAndPassword.account.caseCount
    authentication.account.accountCreationStep =
      input.authentication.tokenFromUsernameAndPassword.account.accountCreationStep
    authentication.account.contactType =
      input.authentication.tokenFromUsernameAndPassword.account.contactType
  }
  return authentication
}

export function loadTheReduxStoreWithAuthenticationInfoResetHelper(
  input: ResetPasswordResponse
): Authentication {
  const authentication = getEmptyAuthentication()
  authentication.isAuthenticated = false
  authentication.idToken = input.authentication.confirmResetCredentials.idToken
  authentication.accessToken =
    input.authentication.confirmResetCredentials.accessToken
  authentication.expirationTime =
    input.authentication.confirmResetCredentials.expirationTime
  authentication.requiresMfa =
    input.authentication.confirmResetCredentials.requiresMfa
  authentication.sessionId =
    input.authentication.confirmResetCredentials.sessionId

  if (
    authentication.account &&
    input.authentication.confirmResetCredentials.account
  ) {
    authentication.account.id =
      input.authentication.confirmResetCredentials.account.id
    authentication.account.givenName =
      input.authentication.confirmResetCredentials.account.givenName
    authentication.account.middleName =
      input.authentication.confirmResetCredentials.account.middleName
    authentication.account.familyName =
      input.authentication.confirmResetCredentials.account.familyName
    authentication.account.suffix =
      input.authentication.confirmResetCredentials.account.suffix
    authentication.account.homePhone =
      input.authentication.confirmResetCredentials.account.homePhone

    authentication.account.mobilePhone =
      input.authentication.confirmResetCredentials.account.mobilePhone
    authentication.account.workPhone =
      input.authentication.confirmResetCredentials.account.workPhone
    authentication.account.userName =
      input.authentication.confirmResetCredentials.account.userName
    authentication.account.emailAddress =
      input.authentication.confirmResetCredentials.account.emailAddress
    authentication.account.street =
      input.authentication.confirmResetCredentials.account.street
    authentication.account.zip =
      input.authentication.confirmResetCredentials.account.zip
    authentication.account.city =
      input.authentication.confirmResetCredentials.account.city
    authentication.account.state =
      input.authentication.confirmResetCredentials.account.state
    authentication.account.ssn =
      input.authentication.confirmResetCredentials.account.ssn
    authentication.account.caseCount =
      input.authentication.confirmResetCredentials.account.caseCount
    authentication.account.accountCreationStep =
      input.authentication.confirmResetCredentials.account.accountCreationStep
    authentication.account.contactType =
      input.authentication.confirmResetCredentials.account.contactType
  } else {
    authentication.account = null
  }
  return authentication
}

// Common Login Helpers finish

// Used with the Profiler API
// https://reactjs.org/docs/profiler.html
export const onRenderCallback = (
  id: string, // the "id" prop of the Profiler tree that has just committed
  phase: 'mount' | 'update', // either "mount" (if the tree just mounted) or "update" (if it re-rendered)
  actualDuration: number, // time spent rendering the committed update
  baseDuration: number, // estimated time to render the entire subtree without memoization
  startTime: number, // when React began rendering this update
  commitTime: number, // when React committed this update
  interactions: Set<Interaction> // the Set of interactions belonging to this update
): void => {
  console.log(
    'ID:',
    id,
    ' | PHASE:',
    phase,
    ' | ACTUAL-DURATION:',
    actualDuration,
    ' | BASE-DURATION:',
    baseDuration,
    ' | START-TIME:',
    startTime,
    ' | COMMIT-TIME:',
    commitTime,
    ' | INTERACTIONS:',
    interactions
  )
}
